import React, { ReactElement, useRef } from 'react'
import cx from 'classnames'
import * as styles from '../../styles/components/content-blocks/text-grid.module.scss'
import reactElementToString from '../../utils/react-element-to-string'
import Heading from '../heading'
import SmallTitle from '../small-title'
import CaptionText from '../caption-text'
import ContentWrapper from '../content-wrapper'
import { motion, useScroll, useTransform } from 'framer-motion'
import { bezierEaseIn } from '../../utils/styles'
import CrosshairLink from '../crosshair-link'
import Video from '../video'
import { FeaturedLink } from '../../utils/types'
import { ContentBlockBase } from './shared'

export interface Text {
    title?: string
    body: ReactElement | string
    vimeoId?: string
    image?: string
    featuredLink?: FeaturedLink
}

export interface TextGridBlockProps extends ContentBlockBase {
    title?: string
    texts: Text[]
    columnCount?: 2 | 3
}

const TextGridBlock = ({ blockId, title, texts, columnCount }: TextGridBlockProps) => {
    const targetRef = useRef<HTMLDivElement>(null)
    const { scrollYProgress } = useScroll({ target: targetRef, offset: ['start end', 'start start'] })
    const opacity = useTransform(scrollYProgress, [0.12, 0.62], [0, 1.0], { ease: bezierEaseIn })

    return (
        <>
            <ContentWrapper id={blockId} ref={targetRef} className={styles.container}>
                <motion.span style={{ opacity }}>
                    {title && <Heading className={styles.heading}>{title}</Heading> }
                </motion.span>

                <div className={cx(styles.textsContainer, styles[`colCount${columnCount}`])}>
                    {texts.map((text, index) =>
                        <div key={index}>
                            <motion.div className={styles.contentContainer} style={{ opacity }}>
                                <div>
                                    {text.title &&
                                        <SmallTitle className={styles.title} as="h3">
                                            {text.title}
                                        </SmallTitle>
                                    }
                                    <CaptionText
                                        className={styles.body}
                                        dangerouslySetInnerHTML={{ __html: reactElementToString(text.body) }}
                                    />
                                </div>

                                <div>
                                    {text.vimeoId &&
                                    <div className={styles.videoContainer}>
                                        <Video vimeoId={text.vimeoId} />
                                    </div>
                                    }
                                    {text.image &&
                                    <div className={styles.imageContainer}>
                                        <img src={text.image} role="presentation" className={styles.image} />
                                    </div>
                                    }
                                    {text.featuredLink &&
                                    <CrosshairLink
                                        as={text.featuredLink.internalLink ? 'internalLink' : 'externalLink'}
                                        href={text.featuredLink.href}
                                        label={text.featuredLink.label}
                                        className={styles.link}
                                        colorVariant="dark"
                                        openInNewTab={text.featuredLink.openInNewTab}
                                    />
                                    }
                                </div>
                            </motion.div>
                        </div>,
                    )}
                </div>
            </ContentWrapper>
        </>
    )
}

export default TextGridBlock
